import PropTypes from "prop-types";
import { Fragment, useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { GlobalModalsContext } from "../../context";
import { useApoAuth, usePropertyLangTag } from "../../hooks";
import jssVariables from "../../jss_setup";
import { getNavbarGenSectionsSelector } from "../../selectors";
import { getProductCategoriesSelector } from "../../umbilical_hocs";
import { isValidURL } from "../../umbilical_tools";
import { QuotationChatApprovalModal } from "../modals/quotationchatapproval";
import { itemStyles, menuItemStyles } from "../NavBar";

const styles = createUseStyles({
  item: (props) => ({
    ...itemStyles,
    ...(props?.customStyles?.item ?? {}),
  }),
  doubleItem: () => ({ fontSize: "small" }),
  selectedItem: (props) => ({
    ...(props?.customStyles?.selectedItem ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    item: (props: any) => ({
      ...itemStyles.smallscreen,
      display: "grid",
      gridTemplateColumns: "auto auto",
      ...(props?.customStyles?.item?.smallscreen ?? {}),
    }),
    doubleItem: () => ({ fontSize: "small" }),
    selectedItem: (props: any) => ({
      "&  strong": {
        fontWeight: "bold",
      },
      ...(props?.customStyles?.selectedItem?.smallscreen ?? {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    item: (props: any) => ({
      ...itemStyles.bigscreen,
      display: "grid",
      gridTemplateColumns: "auto auto",
      ...(props?.customStyles?.item?.bigscreen ?? {}),
    }),
    doubleItem: () => ({ fontSize: "small" }),
  },
});
export function GenericSectionsLinks({ customStyles = {} }) {
  const langTag = usePropertyLangTag();
  const history = useHistory();
  const classes = styles({ customStyles });
  const location = useLocation();
  const { setShowContactUsModal } = useContext(GlobalModalsContext) as {
    setShowContactUsModal: (show: boolean) => void;
  };

  const [__, path] = location.pathname.split("/");

  const { ordered_sections, categories } = useSelector((state) => {
    const ordered_sections = getNavbarGenSectionsSelector(state);
    const categories = getProductCategoriesSelector(state);
    return { ordered_sections, categories };
  });

  const { appUserData } = useApoAuth();
  const showQuotationChat =
    appUserData?.entity_type === "member" ||
    (appUserData?.entity_type === "subagent" && appUserData?.subagent_approved === true);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  return (
    <Fragment>
      {(ordered_sections ?? []).map((section, idx) => {
        const title = section?.[`title${langTag}`] ?? section?.[`header${langTag}`] ?? "";
        const subtitle = section?.[`subtitle${langTag}`] ?? "";
        const hasSubtitle = subtitle !== "";

        const phone = title.replace(/\D/g, "");
        let subphone = null;
        if (hasSubtitle) subphone = subtitle.replace(/\D/g, "");

        return (
          <button
            key={`${idx}-${section.path}`}
            type="button"
            className={`${classes.item} ${path === section.path && classes.selectedItem}${
              hasSubtitle ? ` ${classes.doubleItem}` : ""
            }`}
            disabled={path === section.path}
            onClick={() => {
              if (section.path === "contact_us") {
                setShowContactUsModal(true);
              } else if (isValidURL(section.path)) {
                window.open(section.path, "_blank");
              } else {
                history.push(`/${section.path}`);
              }
            }}
          >
            {!!phone ? (
              <a href={`tel:${phone}`}>
                <strong>{title}</strong>
              </a>
            ) : (
              <span>
                <strong>{title}</strong>
              </span>
            )}
            {hasSubtitle && (
              <Fragment>
                <br />
                {!!subphone ? (
                  <a href={`tel:${subphone}`}>
                    <strong>{subtitle}</strong>
                  </a>
                ) : (
                  <span>
                    <strong>{subtitle}</strong>
                  </span>
                )}
              </Fragment>
            )}
          </button>
        );
      })}
      {categories.includes("QUO") && (
        <button
          type="button"
          className={classes.item}
          onClick={() => {
            if (showQuotationChat) {
              history.push("/quotation-request-chat");
            } else {
              setShowApprovalModal(true);
            }
          }}
        >
          <span>
            <strong>Quotation Chat</strong>
          </span>
        </button>
      )}
      {showApprovalModal && (
        <QuotationChatApprovalModal onClose={() => setShowApprovalModal(false)} />
      )}
    </Fragment>
  );
}
GenericSectionsLinks.propTypes = {
  customStyles: PropTypes.object.isRequired,
};

const mobileStyles = createUseStyles({
  menuItem: (props: any) => ({
    ...menuItemStyles,
    color: jssVariables.colors.text.light,
    textTransform: "capitalize",
    ...(props?.customStyles?.menuItem ?? {}),
  }),
});
export function GenericSectionsLinksMobile({
  customStyles,
  onHide,
  closeModal,
}: {
  customStyles: Record<string, unknown>;
  onHide: () => void;
  closeModal: () => void;
}) {
  const langTag = usePropertyLangTag();
  const history = useHistory();
  const { setShowContactUsModal } = useContext(GlobalModalsContext) as {
    setShowContactUsModal: (show: boolean) => void;
  };
  const classes = mobileStyles({ customStyles });

  const { ordered_sections, categories } = useSelector((state) => {
    const ordered_sections = getNavbarGenSectionsSelector(state);
    const categories = getProductCategoriesSelector(state);
    return { ordered_sections, categories };
  });

  const { appUserData } = useApoAuth();
  const showQuotationChat =
    appUserData?.entity_type === "member" ||
    (appUserData?.entity_type === "subagent" && appUserData?.subagent_approved === true);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  return (
    <Fragment>
      {ordered_sections.length > 0 &&
        ordered_sections.map((section, idx) => {
          const title = section?.[`title${langTag}`] ?? section?.[`header${langTag}`];
          const subtitle = section?.[`subtitle${langTag}`] ?? "";
          const hasSubtitle = subtitle !== "";
          return (
            <div
              key={`${idx}-${section.path}`}
              className={classes.menuItem}
              onClick={() => {
                if (section.path === "contact_us") {
                  setShowContactUsModal(true);
                } else if (isValidURL(section.path)) {
                  window.open(section.path, "_blank");
                } else {
                  history.push(`/${section.path}`);
                }
                onHide();
              }}
            >
              <h4>
                <strong>{title}</strong>
              </h4>
              {hasSubtitle && (
                <h4>
                  <strong>{subtitle}</strong>
                </h4>
              )}
            </div>
          );
        })}
      {categories.includes("QUO") && (
        <button
          type="button"
          className={classes.menuItem}
          onClick={() => {
            if (showQuotationChat) {
              closeModal();
              history.push("/quotation-request-chat");
            } else {
              setShowApprovalModal(true);
            }
          }}
        >
          <span>
            <strong>Quotation Chat</strong>
          </span>
        </button>
      )}
      {showApprovalModal && (
        <QuotationChatApprovalModal onClose={() => setShowApprovalModal(false)} />
      )}
    </Fragment>
  );
}
